import React, { FC, useCallback, useId, useRef, useState } from 'react';
import { Box, Flex, IconButton, Text } from '@chakra-ui/react';
import { X } from 'react-feather';
import { useResizeObserver } from 'ts/common/hooks/useResizeObserver';

export interface IBlurExplainerBannerProps {
    title: string;
    body: string;
    closeLabel: string;
    /** Omit for a non-interactive preview: the banner is then hidden from assistive tech */
    onClose?: () => void;
    /** Distance from the bottom edge, so the banner sits above any bottom navigation */
    bottomOffset?: number;
    /** 'absolute' keeps the banner inside a positioned ancestor, e.g. the Studio preview */
    positionStyle?: 'fixed' | 'absolute';
}

/** Explains the mobile screenshot-protection blur and how to move it */
const BlurExplainerBanner: FC<IBlurExplainerBannerProps> = ({
    title,
    body,
    closeLabel,
    onClose,
    bottomOffset = 0,
    positionStyle = 'fixed'
}) => {
    const titleId = useId();
    const isPreview = !onClose;
    const messageRef = useRef<HTMLDivElement>(null);
    const [isMessageScrollable, setIsMessageScrollable] = useState(false);
    const measureMessage = useCallback(
        (message: HTMLElement) =>
            setIsMessageScrollable(message.scrollHeight > message.clientHeight),
        []
    );

    useResizeObserver({ containerRef: messageRef, callback: measureMessage });

    return (
        <Box
            aria-hidden={isPreview || undefined}
            aria-labelledby={titleId}
            as="section"
            bottom={`calc(${bottomOffset}px + var(--chakra-space-4))`}
            data-testid="blur-explainer-banner"
            display="flex"
            flexDirection="column"
            left={2}
            // Long custom text scrolls inside the banner instead of pushing the close button off-screen
            maxHeight={`calc(100% - ${bottomOffset}px - var(--chakra-space-8))`}
            position={positionStyle}
            right={2}
            zIndex="banner"
        >
            <Flex
                alignItems="flex-start"
                background="paper"
                border="1px solid"
                borderColor="border"
                borderRadius="lg"
                boxShadow="raised"
                color="text"
                gap={2}
                minHeight={0}
                paddingY={3}
                paddingLeft={4}
                paddingRight={2}
            >
                <Box
                    ref={messageRef}
                    alignSelf="stretch"
                    aria-labelledby={isMessageScrollable && !isPreview ? titleId : undefined}
                    flex={1}
                    minWidth={0}
                    overflowWrap="anywhere"
                    overflowY="auto"
                    role={isMessageScrollable && !isPreview ? 'group' : undefined}
                    // Lets keyboard users scroll a message too long to fit
                    tabIndex={isMessageScrollable && !isPreview ? 0 : undefined}
                >
                    <Text as="h2" fontSize="md" fontWeight="semibold" id={titleId} margin={0}>
                        {title}
                    </Text>
                    <Text fontSize="sm" marginTop={1}>
                        {body}
                    </Text>
                </Box>
                <IconButton
                    aria-label={closeLabel}
                    color="inherit"
                    icon={<X size={20} />}
                    minWidth="44px"
                    height="44px"
                    padding={0}
                    onClick={onClose}
                    sx={{
                        // The gallery theme's ghost `_focus` rule clears the ring after `_focusVisible` sets it
                        '&:focus:focus-visible': { boxShadow: 'outline' }
                    }}
                    tabIndex={isPreview ? -1 : undefined}
                    variant="ghost"
                />
            </Flex>
        </Box>
    );
};

export default BlurExplainerBanner;
